import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { API, BACKEND_URL, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import PortalLayout from "../components/PortalLayout";
import OnboardingWizard from "../components/OnboardingWizard";
import BookConsultation from "../components/BookConsultation";
import { useWebSocket } from "../hooks/useWebSocket";
import PortalTour from "../components/PortalTour";
import {
  FileText,
  Upload,
  CreditCard,
  ChevronRight,
  CheckCircle,
  Clock,
  MessageSquare,
  CalendarClock,
  ListTodo,
  Users,
  Wifi,
  WifiOff,
  LayoutDashboard,
  BarChart3,
  Menu,
  X,
  Shield,
  ShieldCheck,
  FolderLock,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";

const SIDEBAR_ITEMS = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "insights", label: "Insights", icon: BarChart3 },
  { id: "itr", label: "ITR Filing", icon: FileText, route: "/itr-filing" },
  { id: "accounting", label: "Transactions", icon: Upload, route: "/accounting" },
  { id: "messages", label: "Messages", icon: MessageSquare, route: "/messages" },
  { id: "profiles", label: "Profiles", icon: Users, route: "/profiles" },
  { id: "packages", label: "Packages", icon: CreditCard, route: "/package-selection" },
];

const Dashboard = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activeView, setActiveView] = useState("overview");
  const [showTour, setShowTour] = useState(user?.is_first_login ?? false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const { connect, connected } = useWebSocket();

  const [consultations, setConsultations] = useState([]);

  useEffect(() => { fetchProfile(); fetchConsultations(); connect(); }, [connect]);
  useEffect(() => { if (user?.is_first_login && user?.role === "client") setShowOnboarding(true); }, [user]);

  const handleOnboardingComplete = async () => { setShowOnboarding(false); await checkAuth(); fetchProfile(); };

  const fetchProfile = async () => {
    try {
      const response = await authFetch(`${API}/profile`);
      if (response.ok) setProfile(await response.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchConsultations = async () => {
    try {
      const response = await authFetch(`${API}/consultations`);
      if (response.ok) {
        const data = await response.json();
        setConsultations(data.consultations || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchInsights = async () => {
    if (insights) return;
    setInsightsLoading(true);
    try {
      const res = await authFetch(`${API}/client-insights`);
      if (res.ok) setInsights(await res.json());
    } catch (e) { console.error(e); }
    finally { setInsightsLoading(false); }
  };

  const handleNavClick = (item) => {
    setSidebarOpen(false);
    if (item.route) { navigate(item.route); return; }
    setActiveView(item.id);
    if (item.id === "insights") fetchInsights();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  const serviceType = profile?.active_service;
  const serviceInfo = profile?.service_info || {};
  const paymentsByFy = profile?.payments_by_fy || {};
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const startFY = currentMonth >= 3 ? currentYear : currentYear - 1;
  const fyOptions = Array.from({ length: 5 }, (_, i) => { const yr = startFY + i - 3; return `${yr}-${yr + 1}`; }).reverse();
  const currentFY = `${startFY}-${startFY + 1}`;
  const currentFYPayment = paymentsByFy[currentFY];
  const effectiveServiceType = currentFYPayment?.service_type || serviceType;
  const showItr = effectiveServiceType === "itr_filing" || effectiveServiceType === "both";
  const showAccounting = effectiveServiceType === "accounting" || effectiveServiceType === "both";

  const tourSteps = [
    { target: '[data-testid="sidebar-dashboard"]', content: 'Here you can see the Dashboard overview.' },
    ...(showItr ? [{ target: '[data-testid="sidebar-itr-filing"]', content: 'Access ITR Filing services here.' }] : []),
    ...(showAccounting ? [{ target: '[data-testid="sidebar-accounting"]', content: 'View your Accounting transactions.' }] : []),
    { target: '[data-testid="sidebar-messages"]', content: 'Check your messages and notifications.' },
    { target: '[data-testid="sidebar-profiles"]', content: 'Manage your family and business profiles here.' },
    { target: '[data-testid="sidebar-services"]', content: 'Select and manage your service packages.' },
  ];

  // ==================== SIDEBAR ====================
  const Sidebar = ({ mobile = false }) => (
    <nav className={`${mobile ? "" : "hidden lg:flex"} flex-col w-64 shrink-0`}>
      <div className="sticky top-20 space-y-2">
        <div className="px-3 mb-4">
          <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-muted)" }}>Navigation</p>
        </div>
        {SIDEBAR_ITEMS.map((item) => {
          const isActive = !item.route && activeView === item.id;
          const Icon = item.icon;
          const disabled = (item.id === "itr" && serviceType && !showItr) || (item.id === "accounting" && serviceType && !showAccounting);
          return (
            <button
              key={item.id}
              onClick={() => !disabled && handleNavClick(item)}
              disabled={disabled}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-[20px] text-sm font-semibold transition-all cursor-pointer group
                ${isActive ? "bg-[var(--primary)] text-white shadow-[0_8px_20px_rgba(20,88,134,0.2)] transform -translate-y-[1px]" : "hover:bg-[var(--primary)]/5 text-[var(--text-main)]"}
                ${disabled ? "opacity-30 cursor-not-allowed" : ""}
              `}
              data-testid={`sidebar-${item.id}`}
            >
              <Icon className={`w-5 h-5 shrink-0 ${isActive ? "text-white" : "text-[var(--primary)] group-hover:text-[var(--primary)]"}`} />
              <span>{item.label}</span>
              {item.id === "messages" && (profile?.unread_messages || 0) > 0 && (
                <span className="ml-auto w-5 h-5 bg-[var(--accent)] text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                  {profile.unread_messages}
                </span>
              )}
              {item.route && <ArrowUpRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-60 transition-opacity" />}
            </button>
          );
        })}

        {/* Security Badge - Charles Style */}
        <div className="mt-4 mx-3 p-4 rounded-[20px] bg-white border border-[var(--border-light)] shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-[var(--success)]" />
            <span className="text-[11px] uppercase tracking-wider font-bold text-[var(--success)]">Secured Vault</span>
          </div>
          <p className="text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
            AES-256 encryption. Documents are private & completely secure.
          </p>
        </div>
      </div>
    </nav>
  );

  // ==================== OVERVIEW VIEW ====================
  const OverviewView = () => (
    <>
      {/* Welcome */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-3">
        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
        <p style={{ color: "var(--text-secondary)" }} className="flex items-center gap-2 text-sm">
          {serviceType ? "Here's an overview of your active services." : "Get started by selecting a service package."}
          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${connected ? "bg-[#55B848]/10 text-[#55B848]" : "bg-[#94A3B8]/10 text-[#94A3B8]"}`} data-testid="ws-status">
            {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {connected ? "Live" : "Offline"}
          </span>
        </p>
      </motion.div>

      {/* 1. ACTIVE SERVICES (Current FY) */}
      {currentFYPayment ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="soft-card mb-3" data-testid="active-services-card">
          <h4 className="text-xs font-bold mb-4 uppercase tracking-wider text-[var(--text-muted)]">Active Services — FY {currentFY}</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            {showItr && (
              <div className="p-4 rounded-[20px] bg-[var(--background-primary)] border border-[var(--border-light)] hover:border-[var(--primary)] transition-all cursor-pointer group"
                onClick={() => navigate("/itr-filing")}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:shadow-md transition-shadow">
                    <FileText className="w-5 h-5 text-[var(--primary)]" />
                  </div>
                  <span className="font-bold text-base text-[var(--text-main)]">ITR Filing</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] mt-3">
                  <CalendarClock className="w-4 h-4 text-[var(--accent)]" />
                  <span>Due: <strong>{serviceInfo.filing_due_date || "Not set"}</strong></span>
                </div>
              </div>
            )}
            {showAccounting && (
              <div className="p-4 rounded-[20px] bg-[var(--background-primary)] border border-[var(--border-light)] hover:border-[var(--primary)] transition-all cursor-pointer group"
                onClick={() => navigate("/accounting")}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:shadow-md transition-shadow">
                    <Upload className="w-5 h-5 text-[var(--primary)]" />
                  </div>
                  <span className="font-bold text-base text-[var(--text-main)]">Accounting</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] mt-3">
                  <ListTodo className="w-4 h-4 text-[var(--accent)]" />
                  <span className="truncate">Pending: {serviceInfo.pending_items?.length > 0 ? serviceInfo.pending_items.join(", ") : "All caught up!"}</span>
                </div>
              </div>
            )}
          </div>
          {/* Modify Package */}
          <div className="mt-5 pt-4 border-t border-[var(--border-light)] flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--text-muted)]">
              {currentFYPayment.service_type === "itr_filing" ? "ITR Filing" : currentFYPayment.service_type === "accounting" ? "Accounting" : "Complete Package"} — {"\u20B9"}{currentFYPayment.total_amount?.toLocaleString()}
            </span>
            {currentFYPayment.service_type !== "both" && (
              <button onClick={() => navigate(`/package-selection?fy=${currentFY}&upgrade=true`)}
                className="soft-btn-outline !py-2 !px-4 text-sm !border-[var(--accent)] !text-[var(--accent)] hover:!bg-[var(--accent)] hover:!text-white" data-testid="modify-package-btn">
                Upgrade to Both
              </button>
            )}
          </div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="soft-card mb-3 !bg-[var(--primary)] !text-white shadow-[0_10px_30px_rgba(20,88,134,0.3)]" data-testid="get-started-card">
          <div className="flex items-center gap-5 mb-4">
            <div className="w-12 h-12 bg-white/10 backdrop-blur rounded-full flex items-center justify-center border border-white/20">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold">Get Started</h3>
              <p className="text-white/80 text-sm mt-1">Select a package to begin your tax journey</p>
            </div>
          </div>
          <button onClick={() => navigate("/package-selection")}
            className="soft-btn !bg-white !text-[var(--primary)] hover:!bg-white hover:scale-[1.02] mt-2 shadow-lg" data-testid="select-package-btn">
            Select Package 
            <div className="arrow-circle bg-[var(--primary)] !text-white !w-6 !h-6 !ml-3"><ChevronRight className="w-3 h-3" /></div>
          </button>
        </motion.div>
      )}

      {/* WORK STATUS CARD */}
      {serviceInfo && serviceType && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="soft-card mb-3" data-testid="work-status-card">
          <h4 className="text-xs font-bold mb-3 uppercase tracking-wider text-[var(--text-muted)]">Your Filing Status</h4>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
              serviceInfo.work_status === "completed" ? "bg-[#10B981]/15" : "bg-[#F59E0B]/15"
            }`}>
              {serviceInfo.work_status === "completed"
                ? <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                : <Clock className="w-6 h-6 text-[#F59E0B]" />}
            </div>
            <div className="flex-1">
              <p className="font-bold text-base text-[var(--text-main)]">
                {serviceInfo.work_status === "completed" ? "Work Completed " : "In Progress"}
              </p>
              <p className="text-sm text-[var(--text-muted)] mt-0.5">
                {serviceInfo.work_status === "completed"
                  ? "Your CA team has finished processing your case. Check your documents."
                  : "Your CA team is currently working on your case."}
              </p>
            </div>
            <span className={`px-3 py-1.5 text-xs font-semibold rounded-full shrink-0 ${
              serviceInfo.work_status === "completed"
                ? "bg-[#10B981]/15 text-[#10B981]"
                : "bg-[#F59E0B]/15 text-[#F59E0B]"
            }`}>
              {serviceInfo.work_status === "completed" ? "Done" : "Ongoing"}
            </span>
          </div>
        </motion.div>
      )}

      {/* 2. QUICK ACTION TILES */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
        {[
          { label: "Documents", value: profile?.document_count || 0, icon: FileText, desc: "Uploaded files", onClick: () => navigate("/itr-filing"), disabled: serviceType && !showItr },
          { label: "Transactions", value: profile?.transaction_count || 0, icon: Upload, desc: "Recorded entries", onClick: () => navigate("/accounting"), disabled: serviceType && !showAccounting },
          { label: "Messages", value: profile?.unread_messages || 0, icon: MessageSquare, desc: profile?.unread_messages > 0 ? "unread" : "No new", onClick: () => navigate("/messages"), badge: profile?.unread_messages > 0 },
          { label: "Profiles", value: "Manage", icon: Users, desc: "Family & business", onClick: () => navigate("/profiles") },
        ].map((card, i) => (
          <div key={i}
            className={`soft-card !p-5 group
              ${card.disabled ? "opacity-40 pointer-events-none grayscale" : ""}`}
            onClick={card.onClick} data-testid={`quick-${card.label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] ${card.badge ? "bg-[var(--accent)]/10" : "bg-[var(--background-primary)]"}`}>
                <card.icon className={`w-5 h-5 ${card.badge ? "text-[var(--accent)]" : "text-[var(--primary)]"}`} />
              </div>
              <div className="w-8 h-8 rounded-full bg-[var(--background-primary)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1">
                <ChevronRight className="w-4 h-4 text-[var(--primary)]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[var(--text-main)]">{card.value}</p>
            <p className="text-sm font-medium mt-1 text-[var(--text-muted)]">{card.desc}</p>
          </div>
        ))}
      </motion.div>

      {/* CONSULTATIONS */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="mb-3">
        <BookConsultation onBooked={() => fetchConsultations()} />
        
        {consultations.length > 0 && (
          <div className="soft-card mb-3">
            <h3 className="font-bold text-lg mb-5 text-[var(--text-main)]">Upcoming Consultations</h3>
            <div className="space-y-3">
              {consultations.map(cons => (
                <div key={cons.consultation_id} className="flex items-center gap-4 p-4 rounded-[16px] bg-[var(--background-primary)] border border-[var(--border-light)]">
                  <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center shrink-0 border border-[var(--border-light)]">
                    <CalendarClock className="w-6 h-6 text-[var(--primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-base text-[var(--text-main)]">
                      {new Date(cons.scheduled_time).toLocaleString()}
                    </p>
                    <p className="text-sm truncate text-[var(--text-muted)] mt-0.5">
                      Status: <span className="capitalize">{cons.status}</span>
                    </p>
                  </div>
                  {cons.meet_link && (
                    <a href={cons.meet_link} target="_blank" rel="noopener noreferrer"
                      className="soft-btn-outline !py-1.5 !px-4 !text-xs !border-[var(--primary)] !text-[var(--primary)] hover:!bg-[var(--primary)] hover:!text-white shrink-0">
                      Join Meet
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* 3. RECENT ACTIVITY (scrollable) */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="soft-card mb-3" data-testid="recent-activity">
        <h3 className="font-bold text-lg mb-5 text-[var(--text-main)]">Recent Activity</h3>
        <div className="max-h-56 overflow-y-auto pr-2 custom-scrollbar">
          {profile?.payment ? (
            <div className="flex items-center gap-4 p-4 rounded-[16px] bg-[var(--background-primary)] hover:shadow-md transition-all border border-[var(--border-light)] cursor-pointer group">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center shrink-0 border border-[var(--border-light)]">
                <CheckCircle className="w-6 h-6 text-[var(--success)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-base text-[var(--text-main)]">Payment Completed</p>
                <p className="text-sm truncate text-[var(--text-muted)] mt-0.5">
                  {"\u20B9"}{profile.payment.total_amount?.toLocaleString()} — {profile.payment.service_type === "itr_filing" ? "ITR Filing" : profile.payment.service_type === "accounting" ? "Accounting" : "Combined"}
                </p>
              </div>
              {profile.payment.receipt_url && (
                <a href={`${BACKEND_URL}${profile.payment.receipt_url}`} target="_blank" rel="noopener noreferrer"
                  className="soft-btn-outline !py-1.5 !px-4 !text-xs !border-[var(--border-light)] hover:!border-[var(--primary)] shrink-0">
                  Receipt
                </a>
              )}
            </div>
          ) : (
            <div className="text-center py-10 px-4 bg-[var(--background-primary)] rounded-[16px] border border-[var(--border-light)] border-dashed">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white shadow-sm flex items-center justify-center">
                <Clock className="w-8 h-8 text-[var(--text-muted)]/50" />
              </div>
              <p className="font-bold text-lg mb-1 text-[var(--text-main)]">No activity yet</p>
              <p className="text-sm text-[var(--text-muted)]">Activity will appear here after your first transaction</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* 4. SERVICE SUBSCRIPTIONS (all FYs) */}
      {serviceType && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="soft-card" data-testid="service-subscriptions-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[var(--background-primary)] border border-[var(--border-light)]">
                <CreditCard className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-[var(--text-main)]">Service Subscriptions</h3>
                <p className="text-sm text-[var(--text-muted)]">Packages across financial years</p>
              </div>
            </div>
            <button onClick={() => navigate("/package-selection")}
              className="soft-btn !py-2 !px-5 text-sm" data-testid="new-fy-package-btn">
              <CreditCard className="w-4 h-4 mr-2" /> New Year
            </button>
          </div>
          <div className="grid gap-3">
            {fyOptions.map((fy) => {
              const payment = paymentsByFy[fy];
              const isPaid = !!payment;
              const isCurrent = fy === currentFY;
              return (
                <div key={fy} className={`flex items-center justify-between p-4 rounded-[16px] transition-all
                  ${isCurrent ? "bg-[var(--primary)]/5 border-2 border-[var(--primary)]" : "bg-[var(--background-primary)] border border-[var(--border-light)] hover:border-[var(--primary)] hover:shadow-md cursor-pointer"}`}
                  data-testid={`fy-row-${fy}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] bg-white`}>
                      {isPaid ? <CheckCircle className="w-5 h-5 text-[var(--success)]" /> : <Clock className="w-5 h-5 text-[var(--text-muted)]/60" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-bold text-base text-[var(--text-main)]">FY {fy}</span>
                        {isCurrent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--primary)] text-white font-bold tracking-wide">CURRENT</span>}
                      </div>
                      {isPaid ? (
                        <span className="text-sm text-[var(--text-muted)]">
                          {payment.service_type === "itr_filing" ? "ITR Filing" : payment.service_type === "accounting" ? "Accounting" : "Complete Package"} — {"\u20B9"}{payment.total_amount?.toLocaleString()}
                        </span>
                      ) : <span className="text-sm text-[var(--accent)] font-medium">No package selected</span>}
                    </div>
                  </div>
                  {isPaid ? (
                    <span className="text-sm px-4 py-1.5 rounded-full bg-[var(--success)]/10 text-[var(--success)] font-bold">Paid</span>
                  ) : (
                    <button onClick={() => navigate(`/package-selection?fy=${fy}`)}
                      className="soft-btn-outline !py-1.5 !px-5 text-sm" data-testid={`select-pkg-${fy}`}>
                      Select Package
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </>
  );

  // ==================== INSIGHTS VIEW ====================
  const InsightsView = () => (
    <>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-3">
        <h1 className="font-['Playfair_Display'] text-2xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>Account Insights</h1>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Your filing history and account metrics at a glance</p>
      </motion.div>

      {insightsLoading ? (
        <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>
      ) : !insights ? (
        <div className="text-center py-20">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-[#145886]/20" />
          <p style={{ color: "var(--text-muted)" }}>Unable to load insights</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
            {[
              { label: "Total Documents", value: insights.total_documents, sub: "uploaded to vault", color: "var(--primary)" },
              { label: "Active Years", value: insights.active_years, sub: insights.active_fy_list?.slice(0, 2).join(", ") || "None yet", color: "var(--success)" },
              { label: "Total Filings", value: insights.total_filings, sub: "completed services", color: "var(--accent)" },
              { label: "Total Spent", value: `\u20B9${(insights.total_spent || 0).toLocaleString()}`, sub: `since ${insights.member_since ? new Date(insights.member_since).getFullYear() : "joining"}`, color: "#8B5CF6" },
            ].map((kpi, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.05 }}
                className="soft-card !p-5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4 shadow-sm border border-[var(--border-light)]" style={{ background: `white` }}>
                  <div className="w-3 h-3 rounded-full shadow-sm" style={{ background: kpi.color }} />
                </div>
                <p className="text-3xl font-bold text-[var(--text-main)]">{kpi.value}</p>
                <p className="text-sm font-bold text-[var(--text-muted)] mt-1">{kpi.label}</p>
                <p className="text-[11px] mt-1 text-[var(--text-muted)]/70 font-medium uppercase tracking-wider">{kpi.sub}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Additional Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="grid sm:grid-cols-2 gap-4 mb-3">
            <div className="soft-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[var(--background-primary)] border border-[var(--border-light)] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[var(--primary)]" />
                </div>
                <h4 className="font-bold text-lg text-[var(--text-main)]">Profiles</h4>
              </div>
              {insights.profiles?.length > 0 ? (
                <div className="space-y-3">
                  {insights.profiles.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-[16px] border border-[var(--border-light)] bg-[var(--background-primary)]">
                      <span className="font-semibold text-sm text-[var(--text-main)]">{p.full_name}</span>
                      {p.pan_number && <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-light)] text-[var(--text-muted)] font-medium shadow-sm">PAN: {p.pan_number}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border border-dashed border-[var(--border-light)] rounded-[16px] bg-[var(--background-primary)]">
                  <p className="text-sm font-medium text-[var(--text-muted)] mb-3">No profiles created yet</p>
                  <button className="soft-btn-outline !py-1.5 !px-5 text-xs" onClick={() => navigate("/profiles")}>Add Profile</button>
                </div>
              )}
            </div>

            <div className="soft-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[var(--background-primary)] border border-[var(--border-light)] flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[var(--success)]" />
                </div>
                <h4 className="font-bold text-lg text-[var(--text-main)]">Security Status</h4>
              </div>
              <div className="space-y-3">
                {[
                  { label: "Document Vault", status: "Encrypted", ok: true },
                  { label: "Infrastructure", status: "Cloudflare R2", ok: true },
                  { label: "File Access", status: "Presigned URLs", ok: true },
                ].map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-[16px] border border-[var(--border-light)] bg-[var(--background-primary)]">
                    <span className="font-medium text-sm text-[var(--text-muted)]">{s.label}</span>
                    <span className={`text-xs font-bold flex items-center gap-1.5 ${s.ok ? "text-[var(--success)]" : "text-[var(--warning)]"}`}>
                      <CheckCircle className="w-4 h-4" /> {s.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Filing Timeline */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="soft-card">
            <h4 className="font-bold text-lg mb-3 text-[var(--text-main)]">Filing History</h4>
            {insights.filing_timeline?.length > 0 ? (
              <div className="space-y-0 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-[var(--border-light)]">
                {insights.filing_timeline.map((entry, i) => (
                  <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                    {/* Timeline dot */}
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-[var(--primary)] shadow-sm shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" />
                    {/* Content */}
                    <div className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-[20px] bg-[var(--background-primary)] border border-[var(--border-light)] shadow-sm mb-4`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-base text-[var(--text-main)]">FY {entry.financial_year} — {entry.service}</p>
                          <p className="text-sm text-[var(--text-muted)] font-medium mt-0.5">
                            {entry.date ? new Date(entry.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                          </p>
                        </div>
                        <span className="text-base font-bold text-[var(--success)] bg-white px-3 py-1 rounded-full shadow-sm border border-[var(--border-light)]">
                          {"\u20B9"}{(entry.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-4 border border-dashed border-[var(--border-light)] rounded-[20px] bg-[var(--background-primary)]">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white shadow-sm border border-[var(--border-light)] flex items-center justify-center">
                  <FileText className="w-8 h-8 text-[var(--text-muted)]/50" />
                </div>
                <p className="font-bold text-lg mb-1 text-[var(--text-main)]">No filings yet</p>
                <p className="text-sm mb-5 text-[var(--text-muted)]">Your filing history will appear here once you complete your first service</p>
                <button onClick={() => navigate("/package-selection")} className="soft-btn">
                  Get Started <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </>
  );

  return (
    <PortalLayout title="Dashboard">
      {showOnboarding && <OnboardingWizard onComplete={handleOnboardingComplete} />}
      
      <div className="mb-3 flex gap-4 border-b border-[var(--border-light)] pb-2">
        <button onClick={() => setActiveView('overview')} className={`px-4 py-2 font-bold ${activeView === 'overview' ? 'text-[var(--primary)] border-b-2 border-[var(--primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'}`}>Overview</button>
        <button onClick={() => setActiveView('insights')} className={`px-4 py-2 font-bold ${activeView === 'insights' ? 'text-[var(--primary)] border-b-2 border-[var(--primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'}`}>Insights</button>
      </div>

      <div className="flex-1 min-w-0 pb-4">
        {activeView === "overview" && <OverviewView />}
        {activeView === "insights" && <InsightsView />}
      </div>
      
      <PortalTour steps={tourSteps} autoStart={showTour} onTourEnd={() => setShowTour(false)} />
    </PortalLayout>
  );
};

export default Dashboard;
